import { OAuth2Config } from '../types';
import { OAuth2Provider } from './base.provider';
import { GenericOAuth2Provider } from './generic.provider';
import { ProfileFetcherFactory } from '../profile/profile-fetcher-factory';

export type ProviderType = 'google' | 'github' | 'microsoft' | 'outlook' | 'facebook' | 'generic';

export interface ProviderFactory {
  createProvider(type: ProviderType, config: OAuth2Config): OAuth2Provider;
}

export class OAuth2ProviderFactory implements ProviderFactory {
  private static presetConfigs: Record<string, Partial<OAuth2Config>> = {
    google: {
      authorizationUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
      tokenUrl: 'https://oauth2.googleapis.com/token',
      profileUrl: 'https://www.googleapis.com/oauth2/v2/userinfo',
      usePKCE: true,
    },
    github: {
      authorizationUrl: 'https://github.com/login/oauth/authorize',
      tokenUrl: 'https://github.com/login/oauth/access_token',
      profileUrl: 'https://api.github.com/user',
    },
    microsoft: {
      authorizationUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
      tokenUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/token',
      profileUrl: 'https://graph.microsoft.com/v1.0/me',
      usePKCE: true,
    },
    outlook: {
      authorizationUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
      tokenUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/token',
      profileUrl: 'https://graph.microsoft.com/v1.0/me',
      usePKCE: true,
      extraAuthParams: {
        prompt: 'select_account',
      },
    },
    facebook: {
      authorizationUrl: 'https://www.facebook.com/v12.0/dialog/oauth',
      tokenUrl: 'https://graph.facebook.com/v12.0/oauth/access_token',
      profileUrl: 'https://graph.facebook.com/me?fields=id,name,email,picture',
    },
  };

  createProvider(type: ProviderType, config: OAuth2Config): OAuth2Provider {
    const mergedConfig =
      type !== 'generic' ? { ...OAuth2ProviderFactory.presetConfigs[type], ...config } : config;

    // Create appropriate profile fetcher
    const profileFetcher = ProfileFetcherFactory.createProfileFetcher(type, mergedConfig);

    // Create provider with profile fetcher
    return new GenericOAuth2Provider(
      mergedConfig,
      undefined, // Use default auth URL strategy
      undefined, // Use default token strategy
      profileFetcher,
    );
  }

  static registerPreset(name: string, config: Partial<OAuth2Config>): void {
    OAuth2ProviderFactory.presetConfigs[name] = config;
  }

  static getPresetConfig(name: string): Partial<OAuth2Config> | undefined {
    return OAuth2ProviderFactory.presetConfigs[name];
  }
}
